// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { AppConfig } from "../../config/config";
import axios, { HttpStatusCode } from "axios";
import { APIService } from "../../utils/apiService";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { RequestState } from "../../utils/types";
import { enqueueSnackbarMessage } from "../../slices/commonSlice/common";

interface CustomHiringLocationMap {
  location: string;
  customMap: string[];
}

interface InterviewStageMap {
  stage: string;
  interviewType: string;
}

export interface AppConfigState {
  state: RequestState;
  stateMessage: string | null;
  errorMessage: string | null;
  recruitmentCycles: string[];
  globalHiringTitle: string | null;
  jdAdditionalContent: string | null;
  customHiringLocationMap: CustomHiringLocationMap[];
  scorecardDefaults: string[];
  interviewStageMap: InterviewStageMap[];
}

export interface AppConfigResponse {
  recruitmentCycles: string[];
  globalHiringTitle: string;
  jdAdditionalContent: string;
  customHiringLocationMap: CustomHiringLocationMap[];
  scorecardDefaults: string[];
  interviewStageMap: InterviewStageMap[];
}

const initialState: AppConfigState = {
  state: RequestState.IDLE,
  stateMessage: null,
  errorMessage: null,
  recruitmentCycles: [],
  globalHiringTitle: null,
  jdAdditionalContent: null,
  customHiringLocationMap: [],
  scorecardDefaults: [],
  interviewStageMap: [],
};

export const fetchAppConfig = createAsyncThunk(
  "appConfig/fetchAppConfig",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await APIService.getInstance().get<AppConfigResponse>(
        AppConfig.serviceUrls.appConfig,
      );

      return {
        recruitmentCycles: response.data.recruitmentCycles,
        globalHiringTitle: response.data.globalHiringTitle,
        jdAdditionalContent: response.data.jdAdditionalContent,
        customLocationMap: response.data.customHiringLocationMap,
        scorecardDefaults: response.data.scorecardDefaults ?? [],
        interviewStageMap: response.data.interviewStageMap,
      };
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        dispatch(
          enqueueSnackbarMessage({
            message:
              error.response?.status === HttpStatusCode.InternalServerError
                ? "Server error while fetching config."
                : "Unexpected error occurred.",
            type: "error",
          }),
        );
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch app config",
        );
      }

      return rejectWithValue("Unknown error");
    }
  },
);

const appConfigSlice = createSlice({
  name: "appConfig",
  initialState,
  reducers: {
    resetAppConfigState(state) {
      state.state = RequestState.IDLE;
      state.errorMessage = null;
      state.stateMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppConfig.pending, (state) => {
        state.state = RequestState.LOADING;
        state.stateMessage = "Fetching application configurations...";
      })
      .addCase(fetchAppConfig.fulfilled, (state, action) => {
        state.state = RequestState.SUCCEEDED;
        state.stateMessage = "Successfully fetched app configurations!";
        state.recruitmentCycles = action.payload.recruitmentCycles;
        state.globalHiringTitle = action.payload.globalHiringTitle;
        state.jdAdditionalContent = action.payload.jdAdditionalContent;
        state.customHiringLocationMap = action.payload.customLocationMap;
        state.scorecardDefaults = action.payload.scorecardDefaults;
        state.interviewStageMap = action.payload.interviewStageMap;
        state.errorMessage = null;
      })
      .addCase(fetchAppConfig.rejected, (state, action) => {
        state.state = RequestState.FAILED;
        state.stateMessage = "Failed to fetch application configurations.";
        state.errorMessage = action.payload as string;
      });
  },
});

export const { resetAppConfigState } = appConfigSlice.actions;
export default appConfigSlice.reducer;
